import { ChevronUpIcon, CommandLineIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Allotment } from 'allotment'
import clsx from 'clsx'
import { Button } from '@criteria-labs/react'
import { useState } from 'react'
import { APIExplorerSettingsButton } from '../api-explorer/APIExplorerSettings'

import 'allotment/dist/style.css'
import './ConsoleLayout.css'

function ConsoleButton({ className, onClick }: { className?: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      className={clsx(
        className,
        'flex items-center gap-0.5 rounded p-2 shadow transition hover:-translate-y-1 hover:shadow-zinc-300 dark:hover:shadow-zinc-700',

        'active:indigo-600 bg-indigo-500 text-indigo-100 hover:text-white'
      )}
      aria-label="Show Console"
      onClick={onClick}
    >
      <CommandLineIcon className="size-5" />
      <ChevronUpIcon className="ml-1 size-3" />
    </button>
  )
}

export function ConsoleTab({
  selected,
  className,
  ...props
}: {
  selected: boolean
} & React.ComponentProps<'button'>) {
  return (
    <button
      className={clsx([
        className,

        // Base
        'inline-flex items-center justify-center gap-x-2 rounded-lg border font-semibold',

        // Sizing
        'px-[calc(theme(spacing.3)-1px)] py-[calc(theme(spacing[1.5])-1px)]',

        // Focus
        'focus:outline-none focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-blue-500',

        // Disabled
        'disabled:opacity-50',

        // Icon
        '[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-1 [&>[data-slot=icon]]:size-4 [&>[data-slot=icon]]:shrink-0',

        // Base
        'border-transparent',

        'text-zinc-500 hover:text-zinc-700 active:text-zinc-700 dark:text-white/70 dark:hover:text-white dark:active:text-white',
        selected
          ? 'bg-indigo-200 hover:bg-indigo-300 active:bg-indigo-300 dark:bg-indigo-800 dark:hover:bg-indigo-700 dark:active:bg-indigo-700'
          : ''
      ])}
      {...props}
    />
  )
}

export function ConsolePanel({
  className,
  onClose,
  header,
  showingSettings,
  setShowingSettings,
  children
}: {
  className?: string
  onClose?: () => void
  header?: React.ReactNode
  showingSettings: boolean
  setShowingSettings: (showingSettings: boolean) => void
  children?: React.ReactNode
}) {
  return (
    <div
      className={clsx(
        className,
        'flex h-full w-full flex-col border-t border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-950'
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-zinc-950/10 bg-indigo-50 p-2 px-2 dark:border-white/10 dark:bg-indigo-950">
        {header}
        <div className="flex items-center gap-2 ">
          <APIExplorerSettingsButton selected={showingSettings} onClick={() => setShowingSettings(!showingSettings)} />
          <Button plain aria-label="Close Console" onClick={onClose}>
            <XMarkIcon className="size-5" />
          </Button>
        </div>
      </div>
      <div className="relative h-0 flex-grow overflow-hidden">{children}</div>
    </div>
  )
}

export function ConsoleLayout({
  console,
  children,
  className
}: {
  console?: ({ setOpen }: { setOpen: (open: boolean) => void }) => React.ReactNode
} & React.ComponentPropsWithoutRef<'div'>) {
  const [open, setOpen] = useState(false)

  return (
    <div className={clsx(className, 'relative h-full w-full')}>
      <Allotment
        vertical
        className="consolePaneContainer relative isolate [--focus-border:theme(colors.blue.500)] [--separator-border:theme(colors.zinc.950/10%)] dark:[--separator-border:theme(colors.white/10%)]"
        onVisibleChange={(_index, value) => {
          setOpen(value)
        }}
      >
        <Allotment.Pane className="consolePane">{children}</Allotment.Pane>
        <Allotment.Pane
          visible={open}
          preferredSize={384} // tailwind h-96
          minSize={160}
          className="consolePane"
        >
          {console && console({ setOpen })}
        </Allotment.Pane>
      </Allotment>
      {console && (
        <ConsoleButton
          className={clsx('absolute bottom-6 right-6', open ? 'opacity-0' : 'opacity-100')}
          onClick={() => setOpen(true)}
        />
      )}
    </div>
  )
}
